import { type ReactNode, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  RefreshCw,
  Copy,
  Check,
  RotateCcw,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { EventsRepository } from "@/application/events/events_repository";
import type { EventSource, UnifiedEvent } from "@/application/events/types";
import { ROUTES } from "@/routes";
import { Button } from "@/components/ui/base/button";
import { EventTypeBadge } from "./components/EventTypeBadge";
import { EventProcessingBadge } from "./components/EventProcessingBadge";
import { JsonViewer } from "./components/JsonViewer";
import { AccountLink } from "./components/AccountLink";
import { ProviderPill } from "@/components/ui/ProviderPill";
import { toPillProvider } from "@/components/ui/provider-pill-providers";

const repo = new EventsRepository();

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatUtc(unix: number): string {
  return new Date(unix * 1000).toUTCString();
}

function formatRelative(unix: number): string {
  const now = Date.now();
  const then = unix * 1000;
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay}d ago`;
}

// ── Meta row ─────────────────────────────────────────────────────────────────

function MetaRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1 border-b py-2.5 last:border-b-0">
      <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
        {label}
      </span>
      <span className="text-foreground text-sm break-words">{children}</span>
    </div>
  );
}

function NullMeta() {
  return <span className="text-subtle-foreground">—</span>;
}

// ── Copy button ───────────────────────────────────────────────────────────────

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    void navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label="Copy to clipboard"
      className="text-subtle-foreground hover:text-foreground ml-1.5 inline-flex items-center rounded p-0.5"
    >
      {copied ? (
        <Check className="h-3 w-3 text-green-600" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </button>
  );
}

// ── View ──────────────────────────────────────────────────────────────────────

export function EventDetailView() {
  const { source, id } = useParams<{
    source: string;
    id: string;
  }>();

  const [event, setEvent] = useState<UnifiedEvent | null | "not-found">(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isReplaying, setIsReplaying] = useState(false);

  async function loadEvent() {
    if (!source || !id) return;
    setLoading(true);
    setError(null);
    setEvent(null);
    try {
      const result = await repo.get(source as EventSource, id);
      setEvent(result ?? "not-found");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load event");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, id]);

  async function pollForStatusChange(
    pollSource: "platform" | "connected",
    pollId: string,
  ) {
    const maxAttempts = 15;
    const intervalMs = 2000;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise((r) => setTimeout(r, intervalMs));
      const updated = await repo.get(pollSource, pollId).catch(() => null);
      if (updated && updated.processingStatus !== "pending") {
        setEvent(updated);
        setIsReplaying(false);
        if (updated.processingStatus === "success") {
          toast.success("Event processed successfully");
        } else if (updated.processingStatus === "failed") {
          toast.error(`Processing failed: ${updated.processingError}`);
        }
        return;
      }
    }
    setIsReplaying(false);
    toast.error("Replay timed out — check back shortly");
  }

  async function handleReplay() {
    if (!event || event === "not-found" || !source) return;
    // Replay is a Stripe-event action. A store notification redrives by flipping
    // `replayRequestedAt`, which the store-notification-processor watches — and
    // that processor does not exist yet, so offering the button here would be a
    // control that silently does nothing.
    if (event.source === "store") return;
    setIsReplaying(true);
    try {
      const result = await repo.replayEvent(event.source, event.id);
      if (!result.success) {
        toast.error(result.error ?? "Replay failed");
        setIsReplaying(false);
        return;
      }
      toast.success("Replay triggered — processing...");
      void pollForStatusChange(event.source, event.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Replay failed");
      setIsReplaying(false);
    }
  }

  // ── Loading ──────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <Loader2 className="text-subtle-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-8 text-center">
        <p className="text-sm text-red-700">Failed to load event: {error}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void loadEvent()}
          className="gap-1.5"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Retry
        </Button>
      </div>
    );
  }

  // ── Not found ────────────────────────────────────────────────────────────

  if (event === "not-found") {
    return (
      <div className="bg-card text-muted-foreground rounded-lg border p-8 text-center">
        <p className="text-base font-medium">Event not found</p>
        <p className="mt-1 text-sm">
          The event <span className="font-mono text-xs">{id}</span> does not
          exist in the <span className="font-medium">{source}</span> table.
        </p>
        <Link
          to={ROUTES.EVENTS}
          className="mt-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to events
        </Link>
      </div>
    );
  }

  if (!event) return null;

  const storeProvider = toPillProvider(event.provider);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-6">
      {/* Back nav */}
      <div>
        <Link
          to={ROUTES.EVENTS}
          className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm"
        >
          <ArrowLeft className="h-4 w-4" />
          Events
        </Link>
      </div>

      {/* Header card */}
      <div className="bg-card rounded-lg border p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2">
            <EventTypeBadge type={event.type} />
            <span className="bg-muted text-muted-foreground rounded-full px-2.5 py-0.5 text-xs font-medium capitalize">
              {event.source}
            </span>
            {event.livemode !== null && (
              <span
                className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  event.livemode
                    ? "bg-green-100 text-green-700"
                    : "bg-yellow-100 text-yellow-700"
                }`}
              >
                {event.livemode ? "Live" : "Test"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-muted-foreground font-mono text-sm">
              {event.id}
            </span>
            <CopyButton value={event.id} />
          </div>
        </div>
      </div>

      {/* Processing Status */}
      <div className="bg-card space-y-2 rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-muted-foreground text-sm font-medium">
              Processing Status
            </span>
            <EventProcessingBadge status={event.processingStatus} />
          </div>
          {event.processingStatus !== "success" &&
            event.processingStatus !== "pending" && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleReplay()}
                disabled={isReplaying}
              >
                {isReplaying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Replaying...
                  </>
                ) : (
                  <>
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Replay
                  </>
                )}
              </Button>
            )}
        </div>
        {event.processedAt && (
          <p className="text-subtle-foreground text-xs">
            Processed {new Date(event.processedAt).toLocaleString()}
          </p>
        )}
        {event.processingStatus === "failed" && event.processingError && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {event.processingError}
          </div>
        )}
      </div>

      {/* Processing History */}
      {event.processingAttempts.length > 0 && (
        <details className="bg-card rounded-lg border">
          <summary className="text-foreground cursor-pointer p-4 text-sm font-medium">
            Processing History ({event.processingAttempts.length} attempt
            {event.processingAttempts.length !== 1 ? "s" : ""})
          </summary>
          <div className="divide-y border-t">
            {[...event.processingAttempts].reverse().map((attempt, i) => (
              <div key={i} className="space-y-1 px-4 py-3 text-sm">
                <div className="flex items-center gap-2">
                  {attempt.success ? (
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-600" />
                  )}
                  <span className="font-medium">
                    {attempt.trigger === "replay" ? "Replay" : "Stream"}
                  </span>
                  <span className="text-subtle-foreground">
                    {new Date(attempt.attemptedAt).toLocaleString()}
                  </span>
                  <span className="text-subtle-foreground">
                    ({attempt.durationMs}ms)
                  </span>
                </div>
                <div className="text-subtle-foreground pl-6 text-xs">
                  Handler: <code>{attempt.handlerName}</code>
                </div>
                {attempt.error && (
                  <div className="pl-6 text-xs text-red-600">
                    {attempt.error}
                  </div>
                )}
                {attempt.replayedBy && (
                  <div className="text-subtle-foreground pl-6 text-xs">
                    Replayed by {attempt.replayedBy}
                  </div>
                )}
                <div className="text-subtle-foreground pl-6 text-xs">
                  Request ID:{" "}
                  <code
                    className="hover:text-foreground cursor-pointer"
                    onClick={() => {
                      void navigator.clipboard
                        .writeText(attempt.lambdaRequestId)
                        .then(() => toast.success("Copied request ID"));
                    }}
                    title="Click to copy"
                  >
                    {attempt.lambdaRequestId}
                  </code>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Body: metadata (left) + payload (right) — matches merchant detail pattern */}
      <div className="grid gap-6" style={{ gridTemplateColumns: "1fr 2fr" }}>
        {/* Metadata column */}
        <div className="space-y-4">
          <div className="bg-card rounded-lg p-5">
            <h2 className="text-foreground mb-3 text-sm font-semibold">
              Metadata
            </h2>
            <div className="divide-y">
              <MetaRow label="Source">
                <span className="capitalize">{event.source}</span>
              </MetaRow>

              <MetaRow label="Account">
                {event.account ? (
                  <AccountLink account={event.account} />
                ) : (
                  <NullMeta />
                )}
              </MetaRow>

              <MetaRow label="Created">
                {event.created ? (
                  <span>
                    {formatUtc(event.created)}{" "}
                    <span className="text-subtle-foreground">
                      ({formatRelative(event.created)})
                    </span>
                  </span>
                ) : (
                  <NullMeta />
                )}
              </MetaRow>

              <MetaRow label="API Version">
                {event.apiVersion ? (
                  <span className="font-mono text-xs">{event.apiVersion}</span>
                ) : (
                  <NullMeta />
                )}
              </MetaRow>

              <MetaRow label="Livemode">
                {event.livemode === null ? (
                  <NullMeta />
                ) : (
                  <span>{event.livemode ? "true" : "false"}</span>
                )}
              </MetaRow>

              <MetaRow label="Request ID">
                {event.request?.id ? (
                  <span className="font-mono text-xs">{event.request.id}</span>
                ) : (
                  <NullMeta />
                )}
              </MetaRow>

              <MetaRow label="Idempotency Key">
                {event.request?.idempotencyKey ? (
                  <span className="font-mono text-xs">
                    {event.request.idempotencyKey}
                  </span>
                ) : (
                  <NullMeta />
                )}
              </MetaRow>

              {/* Store-only fields */}
              {event.source === "store" && (
                <MetaRow label="Provider">
                  {storeProvider ? (
                    <ProviderPill provider={storeProvider} />
                  ) : (
                    (event.provider ?? <NullMeta />)
                  )}
                </MetaRow>
              )}

              {/* Platform-only fields */}
              {event.source === "platform" && (
                <>
                  <MetaRow label="Event Source Type">
                    {event.eventSourceType ?? <NullMeta />}
                  </MetaRow>
                  <MetaRow label="Country">
                    {event.country ?? <NullMeta />}
                  </MetaRow>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Payload column */}
        <div className="space-y-6">
          <div className="bg-card rounded-lg p-5">
            <h2 className="text-foreground mb-3 text-sm font-semibold">
              Payload
            </h2>
            <div className="flex flex-col gap-4">
              <JsonViewer value={event.data.object} />
              {event.data.previousAttributes !== null &&
                event.data.previousAttributes !== undefined && (
                  <JsonViewer
                    value={event.data.previousAttributes}
                    label="data.previous_attributes"
                    collapsible
                    defaultOpen={false}
                  />
                )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
